import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import uniqueId from "lodash/uniqueId";

import { NotificationOptions, Notification } from "./types";
import Notifications from "./Notifications";

type Props = {
  children: React.ReactNode;
  notificationTimeout?: number;
};

type NotificationContextType = {
  clearNotification: (id: string) => void;
  createNotification: (options: NotificationOptions) => string;
  createNotificationForTask: (
    task: Promise<unknown>,
    options: NotificationOptions
  ) => void;
};

const defaultNotificationContext = {
  clearNotification: function () {
    // noop
  },
  createNotification: function () {
    return "";
  },
  createNotificationForTask: function () {
    // noop
  },
};

const NotificationContext = createContext<NotificationContextType>(
  defaultNotificationContext
);

export function useNotification(): NotificationContextType {
  return useContext(NotificationContext);
}

export function NotificationProvider(props: Props) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const clearNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const createNotification = useCallback((options: NotificationOptions) => {
    const id = uniqueId("notification-");
    setNotifications((prev) => [...prev, { ...options, id }]);
    return id;
  }, []);

  // Create a notification for a Promise and clear it when the Promise resolves.
  const createNotificationForTask = useCallback(
    (task: Promise<unknown>, options: NotificationOptions) => {
      const id = createNotification({
        // Default to showing the notification until the task resolves.
        timeout: Infinity,
        ...options,
      });
      task.finally(() => clearNotification(id));
    },
    [createNotification, clearNotification]
  );

  const notificationTimeout = props.notificationTimeout || 5000;

  const value = useMemo(
    () => ({
      clearNotification,
      createNotification,
      createNotificationForTask,
    }),
    [clearNotification, createNotification, createNotificationForTask]
  );

  return (
    <>
      <Notifications
        notifications={notifications}
        onDismiss={clearNotification}
        timeout={notificationTimeout}
      />
      <NotificationContext.Provider value={value}>
        {props.children}
      </NotificationContext.Provider>
    </>
  );
}
